import React, { useState, useEffect, useRef } from 'react';
import Icon from '../../../components/AppIcon';

import Button from '../../../components/ui/Button';
import { isFutureDate, formatIndianLongDate } from '../../../utils/dateUtils';

const EventEditPopover = ({ event, position, onClose, onUpdate, onDelete }) => {
  const [editedEvent, setEditedEvent] = useState(event);
  const [dateError, setDateError] = useState('');
  const popoverRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (popoverRef?.current && !popoverRef?.current?.contains(e?.target)) {
        onClose();
      }
    };

    const handleEscape = (e) => {
      if (e?.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [onClose]);

  const handleTimeChange = (e) => {
    setEditedEvent(prev => ({
      ...prev,
      time: e?.target?.value
    }));
  };

  const handleClientChange = (e) => {
    if (event?.type === 'appointment') {
      setEditedEvent(prev => ({
        ...prev,
        client: e?.target?.value
      }));
    } else {
      setEditedEvent(prev => ({
        ...prev,
        project: e?.target?.value
      }));
    }
  };

  const handleValueChange = (e) => {
    setEditedEvent(prev => ({
      ...prev,
      value: parseFloat(e?.target?.value) || 0
    }));
  };

  const handleSave = () => {
    // Validate that the event date is in the future (Asia/Calcutta timezone)
    if (!isFutureDate(editedEvent?.date)) {
      setDateError('Event date must be in the future');
      return;
    }
    
    setDateError('');
    onUpdate(editedEvent);
  };

  const handleDelete = () => {
    if (window.confirm('Are you sure you want to delete this event?')) {
      onDelete(event?.id);
    }
  };

  // Calculate popover position
  const getPopoverStyle = () => {
    const popoverWidth = 320;
    const popoverHeight = 400;
    const padding = 16;
    
    let top = position?.top + position?.height + 8;
    let left = position?.left;

    // Adjust if popover goes off right edge
    if (left + popoverWidth > window.innerWidth - padding) {
      left = window.innerWidth - popoverWidth - padding;
    }

    // Adjust if popover goes off bottom edge
    if (top + popoverHeight > window.innerHeight - padding) {
      top = position?.top - popoverHeight - 8;
    }

    return {
      position: 'fixed',
      top: `${top}px`,
      left: `${left}px`,
      zIndex: 150
    };
  };

  const eventIcon = event?.type === 'appointment' ? 'Calendar' : 'Package';
  const eventColor = event?.type === 'appointment' ?'bg-blue-100 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400' :'bg-yellow-100 dark:bg-yellow-900/20 text-yellow-600 dark:text-yellow-400';

  return (
    <div
      ref={popoverRef}
      style={getPopoverStyle()}
      className="bg-card rounded-xl shadow-lg border border-border w-80 overflow-hidden"
    >
      {/* Header */}
      <div className={`${eventColor} px-4 py-3 flex items-center justify-between`}>
        <div className="flex items-center gap-2">
          <Icon name={eventIcon} size={20} />
          <span className="font-semibold">
            {event?.type === 'appointment' ? 'Appointment' : 'Delivery'}
          </span>
        </div>
        <button
          onClick={onClose}
          className="hover:opacity-70 transition-smooth"
          aria-label="Close"
        >
          <Icon name="X" size={18} />
        </button>
      </div>

      {/* Body */}
      <div className="p-4 space-y-4">
        <div>
          <label className="text-sm font-medium text-foreground mb-2 block">
            {event?.type === 'appointment' ? 'Client' : 'Project'}
          </label>
          <input
            type="text"
            value={event?.type === 'appointment' ? editedEvent?.client : editedEvent?.project}
            onChange={handleClientChange}
            className="w-full px-3 py-2 rounded-lg border border-input bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-ring"
          />
        </div>

        <div>
          <label className="text-sm font-medium text-foreground mb-2 block">
            Time
          </label>
          <input
            type="text"
            value={editedEvent?.time}
            onChange={handleTimeChange}
            placeholder="e.g., 10:00 AM"
            className="w-full px-3 py-2 rounded-lg border border-input bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-ring"
          />
        </div>

        {event?.type === 'appointment' && (
          <div>
            <label className="text-sm font-medium text-foreground mb-2 block">
              Deal Value (₹)
            </label>
            <input
              type="number"
              value={editedEvent?.value || ''}
              onChange={handleValueChange}
              className="w-full px-3 py-2 rounded-lg border border-input bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-ring"
            />
          </div>
        )}

        <div>
          <label className="text-sm font-medium text-foreground mb-2 block">
            Date
          </label>
          <div className="text-sm text-muted-foreground">
            {formatIndianLongDate(event?.date)}
          </div>
          {dateError && (
            <p className="text-xs text-red-600 dark:text-red-400 mt-1">
              {dateError}
            </p>
          )}
        </div>

        <div>
          <label className="text-sm font-medium text-foreground mb-2 block">
            Status
          </label>
          <div className="flex items-center gap-2">
            <div className={`px-3 py-1 rounded-lg text-xs font-medium ${
              event?.status === 'overdue' ?'bg-red-100 dark:bg-red-900/20 text-red-600 dark:text-red-400'
                : event?.status === 'upcoming' ?'bg-green-100 dark:bg-green-900/20 text-green-600 dark:text-green-400' :'bg-yellow-100 dark:bg-yellow-900/20 text-yellow-600 dark:text-yellow-400'
            }`}>
              {event?.status?.charAt(0)?.toUpperCase() + event?.status?.slice(1)}
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="px-4 py-3 bg-muted/30 flex items-center justify-between gap-2">
        <Button
          variant="destructive"
          size="sm"
          onClick={handleDelete}
          iconName="Trash2"
          iconSize={16}
        >
          Delete
        </Button>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={onClose}
          >
            Cancel
          </Button>
          <Button
            variant="default"
            size="sm"
            onClick={handleSave}
            iconName="Check"
            iconSize={16}
          >
            Save
          </Button>
        </div>
      </div>
    </div>
  );
};

export default EventEditPopover;